import { ACCRUALS_ESTIMATE_STATUS } from '../accrualsEstimatesConstants';

/**
 * Split labels like "2100 — Accounts Payable" into code + name.
 */
export function parseGlAccount(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw || raw === '—') return { code: '', name: '' };
  const labeled = raw.match(/^(\d+)\s*[—–-]\s*(.+)$/);
  if (labeled) return { code: labeled[1], name: labeled[2].trim() };
  const spaced = raw.match(/^(\d+)\s+(.+)$/);
  if (spaced) return { code: spaced[1], name: spaced[2].trim() };
  if (/^\d+$/.test(raw)) return { code: raw, name: '' };
  return { code: '', name: raw };
}

function glCodeOnly(value) {
  return parseGlAccount(value).code || '';
}

function glNameOnly(nameValue, codeValue) {
  const fromName = parseGlAccount(nameValue);
  if (fromName.name) return fromName.name;
  const fromCode = parseGlAccount(codeValue);
  return fromCode.name || '';
}

export function mapScheduleApiToRow(row) {
  if (!row) return null;
  return {
    _id: row._id,
    location: row.location || '—',
    vendor: row.vendor || '—',
    glCode: glCodeOnly(row.gl_code) || '—',
    glName: glNameOnly(row.gl_name, row.gl_code) || '—',
    totalAmount: row.total_amount,
    journals: (row.journals || []).map((journal, index) => ({
      jeRef: journal.je_ref || `JE-${String(index + 1).padStart(4, '0')}`,
      description: journal.description || '—',
      amount: journal.amount,
      debitGl: glCodeOnly(journal.debit_gl) || '—',
      creditGl: glCodeOnly(journal.credit_gl) || '—',
    })),
  };
}

export function mapScheduleRowToDetail(row) {
  if (!row) return null;
  const journals = row.journals || [];
  return {
    _id: row._id,
    vendor: row.vendor,
    location: row.location,
    status: ACCRUALS_ESTIMATE_STATUS.RECORDED,
    workbenchStatus: ACCRUALS_ESTIMATE_STATUS.RECORDED,
    amount: row.totalAmount,
    locked: true,
    debitGl: journals[0]?.debitGl || null,
    creditGl: journals[0]?.creditGl || row.glCode,
    poNumber: journals[0]?.jeRef || null,
    rule: row.glName,
    ruleEstimate: row.totalAmount,
    raw: {
      debit_gl: row.glCode,
      credit_gl: journals[0]?.creditGl,
      journals,
    },
    detail: {
      noaQuote: journals
        .map((journal) => `${journal.jeRef} — ${journal.description}`)
        .join('\n') || '—',
    },
  };
}

export function sortScheduleRows(rows, sortId) {
  const list = [...(rows || [])];
  const key = sortId === 'vendor'
    ? 'vendor'
    : sortId === 'gl'
      ? 'glCode'
      : 'location';
  return list.sort((a, b) => String(a[key] || '').localeCompare(String(b[key] || '')));
}

function csvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function buildScheduleCsv(rows) {
  const header = [
    'Location',
    'Vendor',
    'GL Code',
    'GL Name',
    'Total Amount',
    'JE Ref',
    'JE Amount',
    'Debit GL',
    'Credit GL',
  ];
  const lines = [header.join(',')];
  (rows || []).forEach((row) => {
    const journals = row.journals && row.journals.length
      ? row.journals
      : [{ jeRef: '', amount: '', debitGl: '', creditGl: '' }];
    journals.forEach((journal) => {
      lines.push([
        csvCell(row.location),
        csvCell(row.vendor),
        csvCell(row.glCode),
        csvCell(row.glName),
        csvCell(row.totalAmount),
        csvCell(journal.jeRef),
        csvCell(journal.amount),
        csvCell(journal.debitGl),
        csvCell(journal.creditGl),
      ].join(','));
    });
  });
  return lines.join('\n');
}

export function downloadScheduleCsv(rows, filename) {
  const blob = new Blob([buildScheduleCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'accrual-schedule.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
